import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessBoringBucket } from '../../lib/entitlements';
import {
  boringBucketService,
  FounderTradesResponse,
} from '../../services/boringBucketService';
import '../Dashboard.css';

const POLL_MS = 30000;

const money = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pnlColor = (n: number) => (n > 0 ? '#22c55e' : n < 0 ? '#f87171' : '#94a3b8');
const fmtDate = (s?: string) => (s ? String(s).slice(0, 10) : '—');
// Strike column: single strike for naked / long-only, "short / long" for spreads.
const fmtStrikes = (sell?: number | null, buy?: number | null) => {
  if (sell != null && buy != null) return `${sell} / ${buy}`;
  if (sell != null) return String(sell);
  if (buy  != null) return String(buy);
  return '—';
};

/**
 * Founder's Trades — the live-account transparency view. Members only.
 * Polls every 30s while the page is open.
 */
const ADMIN_EMAILS = new Set(['bobby_malik@hotmail.com', 'bobby@bv-holdings.com']);

export const FounderTrades: React.FC = () => {
  const { user, token } = useAuth();
  const isMember = canAccessBoringBucket(user as any);
  const isAdmin = ADMIN_EMAILS.has(((user as any)?.email || '').toLowerCase());
  const [data, setData] = useState<FounderTradesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await boringBucketService.getFounderTrades(token);
      setData(res);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load founder trades');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const reconcile = useCallback(async () => {
    if (!token || reconciling) return;
    setReconciling(true);
    setError(null);
    try {
      await boringBucketService.reconcileFounderTrades(token);
      // Sync wrote a fresh snapshot -- refetch the read endpoint.
      const fresh = await boringBucketService.getFounderTrades(token);
      setData(fresh);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  }, [token, reconciling]);

  useEffect(() => {
    if (!isMember) { setLoading(false); return; }
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [isMember, load]);

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div style={{ padding: '1.5rem' }}>
          <div className="welcome-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h2>Founder's Trades</h2>
              <p>Real-money options positions — what you're following. Updates every 30 seconds.</p>
              <Link to="/boring-bucket" style={{ fontSize: 13, color: '#60a5fa' }}>← Boring Bucket</Link>
            </div>
            {isMember && isAdmin && (
              <button
                onClick={reconcile}
                disabled={reconciling}
                title="Force a fresh sync against TastyTrade (admin only)"
                style={{
                  background: '#1f2937', color: reconciling ? '#6b7280' : '#a78bfa',
                  border: '1px solid #374151', borderRadius: 6, padding: '6px 14px',
                  cursor: reconciling ? 'wait' : 'pointer', fontSize: 13,
                }}
              >
                {reconciling ? 'Reconciling…' : 'Reconcile'}
              </button>
            )}
          </div>

          {!isMember ? (
            <MembershipRequired />
          ) : loading ? (
            <p style={{ color: '#94a3b8' }}>Loading…</p>
          ) : error ? (
            <p style={{ color: '#f87171' }}>{error}</p>
          ) : data ? (
            <>
              <SummaryRow data={data} />
              <Section title={`Open positions (${data.openPositions.length})`}>
                {data.openPositions.length === 0 ? (
                  <Empty>No open positions right now.</Empty>
                ) : (
                  <table style={tableStyle}>
                    <thead><tr>
                      {['Symbol', 'Strategy', 'Contracts', 'Strikes', 'Exp', 'DTE', 'Credit', 'Capital', 'Unreal. P&L'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {data.openPositions.map(p => (
                        <tr key={p.tradeId}>
                          <td style={tdStyle}><strong>{p.underlying}</strong></td>
                          <td style={tdStyle}>{p.strategy}</td>
                          <td style={tdStyle}>{p.quantity ?? '—'}</td>
                          <td style={tdStyle}>{fmtStrikes(p.sellStrike, p.buyStrike)}</td>
                          <td style={tdStyle}>{fmtDate(p.expiration)}</td>
                          <td style={tdStyle}>{p.dte ?? '—'}</td>
                          <td style={tdStyle}>{money(p.credit)}</td>
                          <td style={tdStyle}>{p.capitalAtRisk != null ? money(p.capitalAtRisk) : '—'}</td>
                          <td style={{ ...tdStyle, color: p.unrealizedPnl != null ? pnlColor(p.unrealizedPnl) : '#94a3b8', fontWeight: 600 }}>
                            {p.unrealizedPnl != null ? money(p.unrealizedPnl) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
              <Section title={`Recently closed (${data.recentClosed.length}) — last ${data.historyDays ?? 90} days`}>
                {data.recentClosed.length === 0 ? (
                  <Empty>No closes in the last 30 days.</Empty>
                ) : (
                  <table style={tableStyle}>
                    <thead><tr>
                      {['Symbol', 'Strategy', 'Contracts', 'Exp', 'P&L', 'Return', 'Reason', 'Closed'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {data.recentClosed.map(t => (
                        <tr key={t.tradeId}>
                          <td style={tdStyle}><strong>{t.underlying}</strong></td>
                          <td style={tdStyle}>{t.strategy}</td>
                          <td style={tdStyle}>{t.quantity ?? '—'}</td>
                          <td style={tdStyle}>{fmtDate(t.expiration)}</td>
                          <td style={{ ...tdStyle, color: pnlColor(t.realizedPnl), fontWeight: 600 }}>{money(t.realizedPnl)}</td>
                          <td style={{ ...tdStyle, color: t.returnPct != null ? pnlColor(t.returnPct) : '#94a3b8', fontWeight: 600 }}>
                            {t.returnPct != null ? `${t.returnPct > 0 ? '+' : ''}${t.returnPct}%` : '—'}
                          </td>
                          <td style={tdStyle}>{t.exitReason || '—'}</td>
                          <td style={tdStyle}>{fmtDate(t.exitAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
              <p style={{ color: '#64748b', fontSize: 11, marginTop: 16 }}>
                {data.asOf ? `Synced ${new Date(data.asOf).toLocaleString()}` : 'Awaiting first sync'} ·
                Live positions + closed trades pulled from the real TastyTrade account (manual and
                automated). Closed-trade P&L is gross of fees. Educational transparency only — not a
                recommendation. The Boring Bucket is not a registered investment advisor.
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const retLabel = (pct?: number | null) =>
  pct == null ? undefined : `${pct > 0 ? '+' : ''}${pct}% on capital`;

const SummaryRow: React.FC<{ data: FounderTradesResponse }> = ({ data }) => {
  const s = data.summary;
  const cells: Array<{ label: string; value: string; color?: string; sub?: string }> = [
    { label: 'Open positions', value: String(s.openCount) },
    { label: 'This month P&L', value: money(s.monthRealizedPnl), color: pnlColor(s.monthRealizedPnl), sub: retLabel(s.monthReturnPct) },
    { label: 'YTD P&L', value: money(s.ytdRealizedPnl), color: pnlColor(s.ytdRealizedPnl), sub: retLabel(s.ytdReturnPct) },
    { label: 'Closed YTD', value: String(s.ytdClosedCount) },
  ];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
      {cells.map(c => (
        <div key={c.label} style={{
          flex: '1 1 150px', background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '12px 16px',
        }}>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.color || '#f9fafb', marginTop: 2 }}>{c.value}</div>
          {c.sub && <div style={{ fontSize: 11, color: c.color || '#94a3b8', marginTop: 2 }}>{c.sub}</div>}
        </div>
      ))}
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <h3 style={{ fontSize: '1em', color: '#e5e7eb', margin: '0 0 8px' }}>{title}</h3>
    {children}
  </div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ color: '#64748b', fontSize: 13 }}>{children}</p>
);

const MembershipRequired: React.FC = () => (
  <div style={{
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, padding: '22px 24px',
  }}>
    <h3 style={{ margin: '0 0 8px', color: '#f9fafb' }}>Members only</h3>
    <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 12px' }}>
      Founder's Trades is part of The Boring Bucket mentored membership.
    </p>
    <Link to="/boring-bucket" style={{ color: '#60a5fa', fontSize: 14 }}>About the membership →</Link>
  </div>
);

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13,
  background: 'rgba(255,255,255,0.03)', borderRadius: 8, overflow: 'hidden',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontSize: 11,
  textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid rgba(255,255,255,0.1)',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px', color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.05)',
};

export default FounderTrades;
